<script setup lang="ts">
import {
  breakpointsTailwind,
  useBreakpoints,
  useVModel,
  useWindowSize,
  whenever,
} from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import Modal from '@/components/base/layout/Modal.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import ReviewFormStep from '@/components/reviews/leave-a-review/ReviewFormStep.vue';
import ReviewNotAvailableStep from '@/components/reviews/leave-a-review/ReviewNotAvailableStep.vue';
import ReviewSubmittedStep from '@/components/reviews/leave-a-review/ReviewSubmittedStep.vue';
import SubRatingsStep from '@/components/reviews/leave-a-review/SubRatingsStep.vue';
import TipsForReviews from '@/components/reviews/TipsForReviews.vue';
import { RatingTierWithZero, ReviewCreationPage, SkuForReview } from '@/lib/reviews';

type Steps =
  | 'review-form'
  | 'review-not-available'
  | 'review-submitted'
  | 'sub-ratings-loading'
  | 'sub-ratings-form'
  | 'tips-for-review';

const props = defineProps<{
  isOpen: boolean;
  modelValue: SkuForReview;
  page: ReviewCreationPage;
}>();

const emit = defineEmits<{
  'handle-close': [];
  'review-submitted': [];
  'update:modelValue': [value: SkuForReview];
}>();

const closeIcon = nutshell['img/close.svg'];

const { width: windowWidth } = useWindowSize();
const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');

const skuForReview = useVModel(props, 'modelValue', emit);

const displayTipsForReviewsModal = ref(false);

const initialStep = computed<Steps>(() =>
  skuForReview.value.reviewStatus === 'reviewAllowed' ? 'review-form' : 'review-not-available',
);
const currentStep = ref(initialStep.value);

const setStep = (step: Steps) => {
  currentStep.value = step;
};

const handleClose = () => {
  if (
    currentStep.value === 'review-submitted' ||
    currentStep.value === 'sub-ratings-loading' ||
    currentStep.value === 'sub-ratings-form'
  )
    emit('review-submitted');
  if (currentStep.value === 'tips-for-review') setStep('review-form');
  else {
    emit('handle-close');
  }
};

const handleReviewSubmitted = (rating: RatingTierWithZero) => {
  setStep(rating > 2 ? 'review-submitted' : 'sub-ratings-loading');
};

whenever(
  () => props.isOpen,
  () => {
    currentStep.value = initialStep.value;
  },
);

watch(
  () => windowWidth.value,
  () => {
    if (displayTipsForReviewsModal.value && isMobile.value) {
      setStep('tips-for-review');
      displayTipsForReviewsModal.value = false;
    } else if (currentStep.value === 'tips-for-review' && !isMobile.value) {
      setStep('review-form');
      displayTipsForReviewsModal.value = true;
    }
  },
);
</script>

<template>
  <Modal
    aria-label="Review"
    breakpoint="sm"
    :hasControls="false"
    :hasDefaultPadding="false"
    :isOpen="isOpen"
    width="sm:max-w-4xl"
    @handle-close="handleClose"
  >
    <template v-if="currentStep === 'sub-ratings-loading'" #close-button> <div /></template>
    <template v-else #close-button>
      <UnstyledButton
        class="absolute flex items-center justify-center w-6 h-6 overflow-hidden rounded right-5 top-6"
        @click="handleClose"
      >
        <img alt="close" class="w-4 h-4 shrink-0" data-test="close-modal" :src="closeIcon" />
      </UnstyledButton>
    </template>

    <template v-if="currentStep !== 'sub-ratings-loading'" #header>
      <div aria-live="polite" class="p-4 md:p-6">
        <transition name="fade" mode="out-in">
          <Header4 v-if="currentStep === 'tips-for-review'">
            Tips for writing helpful reviews
          </Header4>
          <Header4 v-else-if="currentStep === 'review-form'">Leave a review</Header4>
          <Header4 v-else-if="currentStep === 'review-submitted'">Review submitted</Header4>
          <Header4 v-else-if="currentStep === 'sub-ratings-form'">
            Sorry we missed the mark!
          </Header4>
          <Header4 v-else>Review not available</Header4>
        </transition>
      </div>
    </template>
    <template #body>
      <transition name="fade" mode="out-in">
        <TipsForReviews v-if="currentStep === 'tips-for-review'" />
        <ReviewFormStep
          v-else-if="currentStep === 'review-form'"
          v-model:skuForReview="skuForReview"
          v-model:displayTipsForReviewsModal="displayTipsForReviewsModal"
          class="p-4 sm:p-6"
          :page="page"
          @display-tips-modal="setStep('tips-for-review')"
          @handle-close="handleClose"
          @review-submitted="handleReviewSubmitted"
        />
        <ReviewSubmittedStep
          v-else-if="currentStep === 'review-submitted'"
          :name="skuForReview.orderSku.itemName"
          @handle-close="handleClose"
        />
        <SubRatingsStep
          v-else-if="currentStep === 'sub-ratings-loading' || currentStep === 'sub-ratings-form'"
          :name="skuForReview.orderSku.itemName"
          :orderSkuId="skuForReview.orderSku.id"
          :token="skuForReview.token"
          @displayed-form="setStep('sub-ratings-form')"
          @handle-close="handleClose"
        />
        <div v-else>
          <ReviewNotAvailableStep
            :createdDate="skuForReview.review?.createdDate"
            :image="skuForReview.image"
            :name="skuForReview.orderSku.itemName"
            :reviewStatus="skuForReview.reviewStatus"
            @handle-close="handleClose"
          />
        </div>
      </transition>
    </template>
  </Modal>
</template>
